import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Dropdown, Spinner } from '@factorialco/f0-react/dist/experimental'
import { IntegrationsSyncRunSyncingDocument } from 'generated/resources/graphql'

import { useCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { aggregateStatuses } from 'modules/integrations/components/SyncFooter/LastSync/Syncing/StatusAggregation'
import { statusesItems } from 'modules/integrations/components/SyncFooter/LastSync/Syncing/statusesItems'
import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'

export const Syncing = () => {
  const { syncLoading, lastSyncRun, configuration, capabilityMode } =
    usePrivateIntegrationsSyncContext()
  const isExportMode = capabilityMode === 'file'
  const [lastFetchedCursor, setLastFetchedCursor] = useState<string | null>(null)
  const [
    fetchSyncRunSyncing,
    { data: syncRunSyncingData, loading: syncRunSyncingLoading, fetchMore },
  ] = useLazyQuery(IntegrationsSyncRunSyncingDocument)

  const lastSyncRunId = lastSyncRun?.id
  const includeSyncable = !!configuration.syncableStatesAggregation

  const pageInfo = syncRunSyncingData?.integrations?.syncableSyncRunsConnection?.pageInfo
  const hasNextPage = pageInfo?.hasNextPage ?? false
  const endCursor = pageInfo?.endCursor

  const fetchMoreCallback = useCallback(() => {
    if (lastFetchedCursor === endCursor) return
    if (!endCursor) return

    setLastFetchedCursor(endCursor)

    fetchMore({
      variables: {
        syncRunId: lastSyncRunId,
        includeSyncable,
        after: endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.integrations?.syncableSyncRunsConnection) {
          return previousResult
        }

        const previousNodes = previousResult.integrations?.syncableSyncRunsConnection?.nodes ?? []
        const newNodes = fetchMoreResult.integrations.syncableSyncRunsConnection.nodes ?? []

        return {
          ...previousResult,
          integrations: {
            ...previousResult.integrations,
            syncableSyncRunsConnection: {
              ...fetchMoreResult.integrations.syncableSyncRunsConnection,
              nodes: [...previousNodes, ...newNodes],
            },
          },
        }
      },
    })
  }, [endCursor, lastSyncRunId, includeSyncable])

  useEffect(() => {
    if (!lastSyncRunId) return

    fetchSyncRunSyncing({
      variables: {
        syncRunId: lastSyncRunId,
        includeSyncable,
        syncableType: configuration.syncableType,
      },
    })
  }, [lastSyncRunId, includeSyncable])

  if (hasNextPage && lastFetchedCursor !== endCursor) {
    fetchMoreCallback()
  }

  if (!lastSyncRunId || syncLoading || syncRunSyncingLoading || hasNextPage)
    return (
      <Box flexDirection="row" gap="s8" alignItems="center" padding="s8">
        <Spinner size="small" />
        <Text.Normal size="200">
          {isExportMode
            ? i18n.t('integrations.exportable_status.exporting')
            : i18n.t('integrations.syncable_status.syncing')}
        </Text.Normal>
      </Box>
    )

  const syncableStates = aggregateStatuses(
    syncRunSyncingData?.integrations?.syncableSyncRunsConnection?.nodes.map(
      (node) => node.syncableState
    ) ?? [],
    configuration.syncableStatesAggregation
  )

  const counter = `${syncableStates.filter((status) => !status.has('syncing')).length} / ${
    syncableStates.length
  }`

  return (
    <Box flexDirection="row" gap="s8" alignItems="center">
      <Dropdown items={statusesItems(syncableStates, isExportMode)}>
        <F0Button
          variant="ghost"
          icon={Spinner}
          label={`${
            isExportMode
              ? i18n.t('integrations.exportable_status.exporting')
              : i18n.t('integrations.syncable_status.syncing')
          } ${counter}`}
        ></F0Button>
      </Dropdown>
    </Box>
  )
}
